import { useMediaQuery } from "@mui/material";
import React, { useEffect, useRef, memo } from "react";
import {
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

export const PieChartPortfolio = ({ chartData }) => {
  const isSmall = useMediaQuery("(max-width:768px)");
  const COLORS = ["#384ef4", "#b060ed", "#16a34a", "#f59e0b"];
  const totalValue = chartData.reduce((acc, entry) => acc + entry.value, 0);

  // Formatter function for the legend text
  // const renderLegendText = (value, entry) => {
  //   const { payload } = entry;
  //   const percentage = ((payload.value / totalValue) * 100).toFixed(0);
  //   return (
  //     <span>
  //       {value} ({percentage}%)
  //     </span>
  //   );
  // };

  const renderLegendText = (value, entry) => {
    const { payload } = entry;

    // 1. Calculate the raw percentage
    const percentageNum = (payload.value / totalValue) * 100;

    // 2. Format logic:
    // - Use Intl.NumberFormat to handle "up to 3 decimals"
    // - maximumFractionDigits: 3 ensures it never goes past 3
    // - minimumFractionDigits: 0 ensures integers don't have .000
    const percentageDisplay = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 3,
    }).format(percentageNum);

    return (
      <span style={{ color: "#4b5563", fontWeight: "500" }}>
        {value} ({percentageDisplay}%)
      </span>
    );
  };

  const renderCustomizedLabel = ({ name, value, percent }) => {
    // Option A: Clean $ with commas and 1 decimal place (Recommended)
    const formattedValue = value.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    });

    // Example: Ethereum ($139,958.2) 20%
    return `${formattedValue}`;
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart className="mx-auto">
        <Pie
          data={chartData}
          cx="50%"
          cy="50%"
          innerRadius={50}
          outerRadius={isSmall ? 100 : 110}
          fill="#b060ed"
          paddingAngle={5}
          dataKey="value"
          label={isSmall ? false : renderCustomizedLabel}
          className="mx-auto"
        >
          {chartData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
          ))}
        </Pie>
        <Tooltip />
        <Legend formatter={renderLegendText} />{" "}
      </PieChart>
    </ResponsiveContainer>
  );
};

// TradingViewWidget.jsx

export function TradingViewCrncyWidget({ currency = "BTC" }) {
  const container = useRef(null);

  useEffect(() => {
    // 1. Clear the container to prevent duplicates
    if (container.current) {
      container.current.innerHTML = "";
    }

    const script = document.createElement("script");
    script.id = "tradingview-widget-script"; // Give it an ID to track it
    script.src =
      "https://s3.tradingview.com/external-embedding/embed-widget-symbol-overview.js";
    script.type = "text/javascript";
    script.async = true;

    // Use JSON.stringify to keep the config clean
    script.innerHTML = JSON.stringify({
      symbols: [["BINANCE:" + currency.toUpperCase() + "USDT" + "|1M"]],
      chartOnly: false,
      width: "100%",
      height: "400", // Note: '100%' requires a parent with defined height
      locale: "en",
      colorTheme: "light",
      autosize: true,
      showVolume: false,
      hideDateRanges: false,
      hideMarketStatus: false,
      hideSymbolLogo: false,
      scalePosition: "right",
      scaleMode: "Normal",
      fontFamily:
        "-apple-system, BlinkMacSystemFont, Trebuchet MS, Roboto, Ubuntu, sans-serif",
      fontSize: "10",
      headerFontSize: "small",
      noTimeScale: false,
      valuesTracking: "1",
      changeMode: "price-and-percent",
      chartType: "area",
      lineColor: "#2962FF",
      bottomColor: "rgba(41, 98, 255, 0)",
      topColor: "rgba(41, 98, 255, 0.3)",
      lineWidth: 2,
    });

    container.current.appendChild(script);

    // 2. Optional: Cleanup function if you want to be extra safe
    return () => {
      if (container.current) {
        container.current.innerHTML = "";
      }
    };
  }, [currency]); // Added currency as dependency so it refreshes on change

  return (
    <div
      className="tradingview-widget-container"
      ref={container}
      style={{ height: "100%", width: "100%" }}
    >
      <div className="tradingview-widget-container__widget"></div>
    </div>
  );
}

export const transformHoldingData = (apiResponse, findCurrency) => {
  if (!apiResponse) return [];

  // 1. Sort the result by totalValue in descending order
  const sortedData = [...apiResponse].sort(
    (a, b) => b.totalValue - a.totalValue,
  );

  // 2. Separate the top 4 holdings
  const top4Raw = sortedData.slice(0, 4);
  const remainingRaw = sortedData.slice(4);

  // 3. Format the top 4 for the chart
  const chartData = top4Raw.map((item) => ({
    // Capitalize the first letter (e.g., 'bitcoin' -> 'Bitcoin')
    name: findCurrency(item._id)?.symbol,
    value: parseFloat(item.totalValue.toFixed(2)), // Format to 2 decimal places
  }));

  // 4. If there are more than 4 coins, group the rest into "Others"
  if (remainingRaw.length > 0) {
    const othersValue = remainingRaw.reduce(
      (acc, item) => acc + item.totalValue,
      0,
    );
    chartData.push({
      name: "Others",
      value: parseFloat(othersValue.toFixed(2)),
    });
  }

  return chartData;
};

// export const transformNetworkData = (networkData) => {
//   if (!networkData) return [];

//   const holdings = [];

//   // 1. Calculate Native Asset (ETH or BNB)
//   const nativeBalance = networkData.ETH?.balance || 0;
//   const nativeRate = networkData.ETH?.price?.rate || 0;
//   const nativeValue = nativeBalance * nativeRate;

//   if (nativeValue > 0) {
//     holdings.push({
//       // Display 'ETH' or 'BNB'. Your data uses 'ETH' key for native in both objects.
//       name: nativeRate > 1000 ? "ETH" : "BNB",
//       value: nativeValue,
//     });
//   }

//   // 2. Calculate Tokens
//   if (networkData.tokens && Array.isArray(networkData.tokens)) {
//     networkData.tokens.forEach((t) => {
//       const info = t.tokenInfo;

//       // Only process tokens with a valid price
//       if (info && info.price && info.price.rate) {
//         const decimals = parseInt(info.decimals) || 18;

//         // Convert large integer balance to human-readable float
//         const actualBalance = t.balance / Math.pow(10, decimals);
//         const tokenValue = actualBalance * info.price.rate;

//         if (tokenValue > 0) {
//           holdings.push({
//             name: info.symbol || info.name,
//             value: tokenValue,
//           });
//         }
//       }
//     });
//   }

//   // 3. Sort by Value Descending
//   const sorted = holdings.sort((a, b) => b.value - a.value);

//   // 4. Top 4 + Others Logic
//   const top4 = sorted.slice(0, 4);
//   const remaining = sorted.slice(4);

//   const finalData = top4.map((item) => ({
//     name: item.name,
//     value: parseFloat(item.value.toFixed(2)),
//   }));

//   if (remaining.length > 0) {
//     const othersSum = remaining.reduce((sum, item) => sum + item.value, 0);
//     finalData.push({
//       name: "Others",
//       value: parseFloat(othersSum.toFixed(2)),
//     });
//   }

//   return finalData;
// };
export const transformNetworkData = (networkData) => {
  if (!networkData) return [];

  const holdings = [];

  // 1. Process Native (ETH/BNB)
  const nativeBalance = networkData.ETH?.balance || 0;
  const nativeRate = networkData.ETH?.price?.rate || 0;
  const nativeValue = nativeBalance * nativeRate;

  // Only push if there's an actual dollar value > 0
  if (nativeValue > 0) {
    holdings.push({
      name: nativeRate > 1000 ? "ETH" : "BNB",
      value: nativeValue,
    });
  }

  // 2. Process Tokens
  if (networkData.tokens && Array.isArray(networkData.tokens)) {
    networkData.tokens.forEach((t) => {
      const info = t.tokenInfo;
      // Filter: Must have a price, a rate, and a balance > 0
      if (info?.price?.rate && t.balance > 0) {
        const decimals = parseInt(info.decimals) || 18;
        const actualBalance = t.balance / Math.pow(10, decimals);
        const tokenValue = actualBalance * info.price.rate;

        if (tokenValue > 0.01) {
          // Ignore dust smaller than 1 cent
          holdings.push({
            name: info.symbol || info.name,
            value: tokenValue,
          });
        }
      }
    });
  }

  // 3. Sort and Apply Top 4 + Others
  const sorted = holdings.sort((a, b) => b.value - a.value);
  const top4 = sorted.slice(0, 4);
  const remaining = sorted.slice(4);

  const finalData = top4.map((item) => ({
    name: item.name,
    value: parseFloat(item.value.toFixed(2)),
  }));

  if (remaining.length > 0) {
    const othersSum = remaining.reduce((sum, item) => sum + item.value, 0);
    finalData.push({ name: "Others", value: parseFloat(othersSum.toFixed(2)) });
  }

  return finalData;
};
export default memo(TradingViewCrncyWidget);
